/**
 * honKitzba.js — compute הון / קצבה split per fund using perPeriod data.
 *
 * Uses PerutYitraLeTkufa (perPeriod) which is the most granular level:
 *   rekiv "1"         → פיצויים
 *   rekiv "2","3","4","10" → תגמולים components
 *   sugItra "1"       → הון (capital, liquid)
 *   sugItra "2"/"3"   → קצבה (pension)
 *
 * Study funds (קרן השתלמות) are entirely הון — uses fund.redeem directly.
 * תיקון 190 (rekiv "10") uses sugItra to split into hon190 / kitzba190.
 * Fallback (קופת גמל only): if fund.tikun190 === "כן" but perPeriod never uses
 * rekiv "10", any kitzba amount is moved to hon190 (clearinghouse data
 * inconsistency). Pension funds are never reclassified — their money is kitzba
 * by law even when the producer reports TIKUN-190=1.
 *
 * The row-level classification (rekiv/sugItra → color) lives in
 * moneyComponentLedger.js — this function reduces those rows into the
 * fund-level aggregate totals every call site here already expects.
 */

import { buildFundLedger } from "./moneyComponentLedger";

/**
 * @param {object} fund — parsed fund object from xmlParser
 * @returns {{ hon, kitzba, hon190, kitzba190, preReform, kitzbaPreSettlement, honPitzuyim, kitzbaPitzuyim, preReformPitzuyim }}
 *   hon                — tagmulim classified as הון (sugItra "1")
 *   kitzba             — tagmulim classified as קצבה (sugItra "2"/"3")
 *   hon190             — portion of hon that is תיקון 190 money (rekiv "10")
 *   kitzba190          — portion of kitzba that is תיקון 190 money (rekiv "10")
 *   preReform          — tagmulim "נתון לבחירת הלקוח" (sugItra "4", typically עד הסדרים)
 *   honPitzuyim        — pitzuyim classified as הון (rekiv "1", sugItra "1")
 *   kitzbaPitzuyim     — pitzuyim classified as קצבה (rekiv "1", sugItra "2"/"3")
 *   preReformPitzuyim  — pitzuyim "נתון לבחירת הלקוח" (rekiv "1", sugItra "4")
 */
export function computeHonKitzba(fund) {
  const CAPITAL_CATS = ["קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד"];
  const empty = { hon: 0, kitzba: 0, hon190: 0, kitzba190: 0, preReform: 0, kitzbaPreSettlement: 0, honPitzuyim: 0, kitzbaPitzuyim: 0, preReformPitzuyim: 0 };

  if (CAPITAL_CATS.includes(fund.cat)) {
    return { ...empty, hon: fund.redeem || 0 };
  }

  const rows = buildFundLedger(fund);

  const result = rows.reduce((acc, r) => {
    switch (r.color) {
      case "תגמולים הון":   acc.hon += r.amount; break;
      case "תיקון 190":
        if (r.sugItra === "2" || r.sugItra === "3") {
          acc.kitzba += r.amount;
          acc.kitzba190 += r.amount;
        } else {
          acc.hon += r.amount;
          acc.hon190 += r.amount;
        }
        break;
      case "תגמולים קצבה":  acc.kitzba += r.amount; break;
      case "נתון לבחירה":   acc.preReform += r.amount; break;
      case "פיצויים הון":   acc.honPitzuyim += r.amount; break;
      case "פיצויים קצבה":  acc.kitzbaPitzuyim += r.amount; break;
      case "נתון לבחירה (פיצויים)": acc.preReformPitzuyim += r.amount; break;
      default: break;
    }
    return acc;
  }, { ...empty });

  // kitzbaPreSettlement (עד חוק ההסדרים) — tracked separately from color,
  // same as the original per-row check on shichva === "1".
  (fund.yitrot?.perPeriod || []).forEach(p => {
    const amt = p.amount || 0;
    if (amt && (p.rekiv === "2" || p.rekiv === "3" || p.rekiv === "4") &&
        (p.sugItra === "2" || p.sugItra === "3") && p.shichva === "1") {
      result.kitzbaPreSettlement += amt;
    }
  });

  return result;
}
